/* Chat launcher gate — hold #rb-launcher back until EITHER the visitor has
   scrolled halfway down the page (REVEAL_AT) OR five seconds have passed
   (REVEAL_AFTER_MS), whichever comes first. Sitewide.

   In practice the timer wins almost every visit — half a page inside five
   seconds is a hard flick. The scroll rule is kept because it reveals sooner
   for someone who does move fast, and it costs nothing.
   ---------------------------------------------------------------------------
   WHY A CLASS ON <html> AND AN INJECTED <style>, rather than a rule in a
   stylesheet: NO THEME FILE RENDERS #rb-launcher. The third-party widget
   injects it into <body> at runtime — confirmed by grep, and recorded at
   sections/pdp-ask-ai.liquid:7. Measured: it enters the DOM at ~3s. So the rule
   has to be in force before an element that does not exist yet appears, which
   is why this file is loaded in <head> and not deferred.

   WHY opacity + pointer-events AND NOT display:none. sections/pdp-ask-ai.liquid
   opens the chat by CLICKING #rb-launcher. Keeping the element laid out and
   merely invisible leaves that path behaving exactly as before, and avoids the
   launcher's absence changing anything the widget measures.

   PROGRESS, NOT GESTURES. This replaced a "count N scroll gestures" rule.
   Counting gestures was unpredictable: a scroll fires ~30 events per flick, and
   iOS momentum emits them in waves, so one hard swipe could satisfy a count of
   two. Progress through the document is what "half the page" actually means and
   it scales to the page rather than to how someone flicks.

   `scrollY / (scrollHeight - innerHeight)` is the fraction of the SCROLLABLE
   DISTANCE travelled — 0 at the top, 1 with the last pixel on screen. That is
   the usual reading of "halfway down the page". It is not the same as the
   viewport's midpoint crossing the document's midpoint, which would fire
   slightly earlier on a long page.

   A PAGE THAT CANNOT SCROLL REVEALS IMMEDIATELY. When scrollHeight <=
   innerHeight the whole page is already on screen, so the threshold is
   meaningless. The timer would cover this case anyway now, but the branch is
   kept: it reveals at once rather than making someone on a one-screen page wait
   five seconds for a chat button that was never going to be scrolled to.

   The height is re-read on every scroll and on resize, because pages here grow
   after load: lazy images, the bundle grid, and third-party embeds all change
   scrollHeight well after DOMContentLoaded. Caching it once would compare
   against a stale denominator and fire early.

   Reverting: delete this file and its <script> tag in layout/theme.liquid.
   Nothing else references it. */

(function () {
  'use strict';

  var REVEAL_AT = 0.5;
  var REVEAL_AFTER_MS = 5000;
  var HIDE_CLASS = 'rb-chat-gated';

  var root = document.documentElement;

  var style = document.createElement('style');
  style.textContent =
    '#rb-launcher{transition:opacity .25s ease}' +
    '.' + HIDE_CLASS + ' #rb-launcher{opacity:0;pointer-events:none}';
  (document.head || root).appendChild(style);

  root.classList.add(HIDE_CLASS);

  var revealed = false;
  var ticking = false;
  var settled = false;

  function reveal() {
    if (revealed) return;
    revealed = true;
    root.classList.remove(HIDE_CLASS);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', check);
  }

  /* NOTHING IS DECIDED BEFORE `load`, and that guard is load-bearing.
     scrollHeight is unreliable while images and embeds are still arriving: a
     long page routinely measures SHORTER than the viewport at script time,
     which would hit the not-scrollable branch and reveal instantly. Reveal is
     one-way, so that mistake is permanent for the page view.

     THE COST IS A DELAY, AND ONLY A DELAY. `load` waits on every third-party
     resource, and this store has many: one measured run had not fired it 11s
     in, while three others were complete inside 5s. A visitor who passes the
     threshold before `load` is caught by the check that runs at `load`, so the
     launcher arrives late rather than never.

     Do NOT "improve" this by settling on DOMContentLoaded plus a delay. That
     trades a late launcher for a wrong one: images are still arriving, the page
     can measure shorter than the viewport, and the reveal would be both instant
     and permanent. Late is the safe direction here. */
  function check() {
    if (revealed || !settled) return;
    var scrollable = root.scrollHeight - window.innerHeight;
    if (scrollable <= 0) { reveal(); return; }   /* whole page already visible */
    if (window.scrollY / scrollable >= REVEAL_AT) reveal();
  }

  /* rAF-throttled: scroll fires far more often than the page can paint, and
     this reads scrollHeight, which forces layout. */
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      check();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', check, { passive: true });

  /* TIME FALLBACK — deliberately NOT gated on `settled`.
     `settled` exists so scrollHeight is not measured while images are still
     arriving. A clock needs no measurement, so making it wait for `load` would
     turn 5s into however long the slowest third-party resource takes — 11s on
     one measured run. Started at script execution, which is in <head>, so it is
     5s from the start of the page rather than 5s after everything settles.

     NOTE ON PRECEDENCE: at 5s this is what reveals the launcher on nearly every
     visit. Half a page in under five seconds is a hard flick, so REVEAL_AT is
     now the exception rather than the rule. That is the point — it guarantees
     the launcher is reachable by someone who never scrolls. If the scroll rule
     should lead again, this number has to grow, not that one. */
  window.setTimeout(reveal, REVEAL_AFTER_MS);

  /* First real measurement. Also catches a page that is short enough to need no
     scrolling, and a back-navigation restored mid-page, neither of which fires
     a scroll event. */
  if (document.readyState === 'complete') {
    settled = true;
    check();
  } else {
    window.addEventListener('load', function () {
      settled = true;
      check();
    });
  }
})();
